import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { emailApiBase, emailApiHeaders } from './store/api.js'
import { buildImageUploadPath } from '@tshmanager/email-shared'

/**
 * Email TipTap — lifecycle helpers for email compose / reply / signature forms.
 *
 * Image pipeline: paste/drop/pick → blob → CDN `{ location }` via pendingUploads / flushTipTapUploads.
 *   paste/drop/pick → blob: src → POST multipart `file` → CDN `{ location }` → replace src
 *   flushTipTapUploads awaits pending uploads before send
 */

/** @typedef {{ editor: Editor, shell: HTMLElement, textarea: HTMLTextAreaElement, pendingUploads: Set<Promise<unknown>>, uploadUrl: string, uploadToEmailApi: boolean, onImageUploadError?: () => string }} TipTapEntry */

/** @type {Map<string, TipTapEntry>} */
const editors = new Map()

const CSRF_TOKEN = () =>
  document.querySelector('meta[name="csrf-token"]')?.content ?? ''

const FORE_COLORS = [
  { label: 'Fekete', value: '#000000' },
  { label: 'Piros', value: '#dc3545' },
  { label: 'Kék', value: '#0d6efd' },
  { label: 'Zöld', value: '#198754' },
  { label: 'Narancs', value: '#fd7e14' },
  { label: 'Szürke', value: '#6c757d' },
]

/**
 * Convert bare &lt;div&gt; blocks to &lt;p&gt; so TipTap keeps paragraph breaks.
 * Matches mobile `normalizeTipTapHtml` in email/src/lib/composeHtml.ts.
 *
 * @param {string} html
 * @returns {string}
 */
export function normalizeTipTapHtml(html) {
  return (html || '')
    .replace(/<div(?![^>]*class=)[^>]*>/gi, '<p>')
    .replace(/<\/div>/gi, '</p>')
}

/**
 * True when the HTML still references browser-local blob: image URLs.
 *
 * @param {string} html
 * @returns {boolean}
 */
export function htmlHasBlobImages(html) {
  return /<img\b[^>]*\bsrc\s*=\s*(["']?)blob:/i.test(html || '')
}

/**
 * @param {string} editorId
 * @returns {Editor|null}
 */
export function getTipTapEditor(editorId) {
  return editors.get(editorId)?.editor ?? null
}

/**
 * Resolve textarea from selector or element.
 * @param {string|HTMLElement} editorElOrSelector
 * @returns {HTMLTextAreaElement|null}
 */
function resolveTextarea(editorElOrSelector) {
  if (!editorElOrSelector) return null
  if (typeof editorElOrSelector === 'string') {
    return document.querySelector(editorElOrSelector)
  }
  return editorElOrSelector
}

/**
 * @param {string|HTMLElement} editorElOrSelector
 * @returns {string|null}
 */
function resolveEditorId(editorElOrSelector) {
  const el = typeof editorElOrSelector === 'string'
    ? document.querySelector(editorElOrSelector)
    : editorElOrSelector
  if (el?.id) return el.id
  if (typeof editorElOrSelector === 'string') {
    return editorElOrSelector.replace(/^#/, '') || null
  }
  return null
}

/**
 * Upload a file; resolves with CDN location string.
 *
 * @param {File|Blob} file
 * @param {string} filename
 * @param {string} uploadUrl
 * @param {boolean} uploadToEmailApi
 * @param {() => string} [onImageUploadError]
 * @returns {Promise<string>}
 */
function uploadImageBlob(file, filename, uploadUrl, uploadToEmailApi, onImageUploadError) {
  return new Promise((resolve, reject) => {
    const formData = new FormData()
    formData.append('file', file, filename)

    const xhr = new XMLHttpRequest()
    xhr.open('POST', uploadUrl)
    xhr.withCredentials = true

    const headers = uploadToEmailApi
      ? emailApiHeaders()
      : { 'X-CSRF-Token': CSRF_TOKEN() }
    Object.entries(headers).forEach(([key, value]) => {
      if (value != null && value !== '') xhr.setRequestHeader(key, String(value))
    })

    xhr.onload = () => {
      const failMsg = onImageUploadError?.() ?? `HTTP Error: ${xhr.status}`
      if (xhr.status === 403 || xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(failMsg))
        return
      }
      let json
      try {
        json = JSON.parse(xhr.responseText)
      } catch (_) {
        reject(new Error(onImageUploadError?.() ?? 'Invalid JSON from image upload'))
        return
      }
      if (!json || typeof json.location !== 'string' || !json.location) {
        reject(new Error(onImageUploadError?.() ?? 'Image upload response missing location'))
        return
      }
      resolve(json.location)
    }

    xhr.onerror = () => {
      reject(new Error(onImageUploadError?.() ?? `Image upload failed (XHR ${xhr.status})`))
    }

    xhr.send(formData)
  })
}

/**
 * Replace an image node whose src matches blobUrl with the CDN location.
 *
 * @param {Editor} editor
 * @param {string} blobUrl
 * @param {string} location
 */
function replaceImageSrc(editor, blobUrl, location) {
  const { state } = editor
  let pos = null
  state.doc.descendants((node, nodePos) => {
    if (pos != null) return false
    if (node.type.name === 'image' && node.attrs.src === blobUrl) {
      pos = nodePos
      return false
    }
    return undefined
  })
  if (pos == null) return
  const node = state.doc.nodeAt(pos)
  if (!node) return
  const tr = state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: location })
  editor.view.dispatch(tr)
}

/**
 * Insert local images as blob: then upload and swap to CDN location.
 *
 * @param {TipTapEntry} entry
 * @param {File[]} files
 */
function ingestImageFiles(entry, files) {
  const { editor, uploadUrl, uploadToEmailApi, onImageUploadError, pendingUploads } = entry
  for (const file of files) {
    if (!file?.type?.startsWith('image/')) continue
    const blobUrl = URL.createObjectURL(file)
    editor.chain().focus().setImage({ src: blobUrl, alt: file.name || 'image' }).run()

    const promise = uploadImageBlob(
      file,
      file.name || 'image.png',
      uploadUrl,
      uploadToEmailApi,
      onImageUploadError,
    ).then((location) => {
      replaceImageSrc(editor, blobUrl, location)
      URL.revokeObjectURL(blobUrl)
      syncEntryToTextarea(entry)
    }).catch((err) => {
      URL.revokeObjectURL(blobUrl)
      console.warn('[TipTap] image upload failed', err)
      throw err
    })

    pendingUploads.add(promise)
    promise.finally(() => pendingUploads.delete(promise))
  }
}

/**
 * @param {TipTapEntry} entry
 */
function syncEntryToTextarea(entry) {
  if (!entry?.editor || !entry?.textarea) return
  const html = entry.editor.getHTML()
  entry.textarea.value = html
  entry.textarea.dispatchEvent(new Event('input', { bubbles: true }))
}

/**
 * Build a compact Hungarian-friendly toolbar.
 *
 * @param {Editor} editor
 * @param {TipTapEntry} entry
 * @returns {HTMLElement}
 */
function buildToolbar(editor, entry) {
  const bar = document.createElement('div')
  bar.className = 'tiptap-toolbar'
  bar.setAttribute('role', 'toolbar')

  const mkBtn = (title, iconClass, onClick, { activeCheck } = {}) => {
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'tiptap-toolbar-btn'
    btn.title = title
    btn.setAttribute('aria-label', title)
    btn.innerHTML = `<i class="${iconClass}"></i>`
    btn.addEventListener('mousedown', (e) => e.preventDefault())
    btn.addEventListener('click', (e) => {
      e.preventDefault()
      onClick()
    })
    if (activeCheck) {
      const syncActive = () => {
        btn.classList.toggle('is-active', !!activeCheck())
      }
      editor.on('selectionUpdate', syncActive)
      editor.on('transaction', syncActive)
      syncActive()
    }
    return btn
  }

  const sep = () => {
    const s = document.createElement('span')
    s.className = 'tiptap-toolbar-sep'
    s.setAttribute('aria-hidden', 'true')
    return s
  }

  bar.append(
    mkBtn('Félkövér', 'bi bi-type-bold', () => editor.chain().focus().toggleBold().run(), {
      activeCheck: () => editor.isActive('bold'),
    }),
    mkBtn('Dőlt', 'bi bi-type-italic', () => editor.chain().focus().toggleItalic().run(), {
      activeCheck: () => editor.isActive('italic'),
    }),
    mkBtn('Aláhúzott', 'bi bi-type-underline', () => editor.chain().focus().toggleUnderline().run(), {
      activeCheck: () => editor.isActive('underline'),
    }),
  )

  // Color dropdown
  const colorWrap = document.createElement('div')
  colorWrap.className = 'tiptap-toolbar-dropdown'
  const colorBtn = mkBtn('Szövegszín', 'bi bi-palette', () => {
    colorWrap.classList.toggle('is-open')
  })
  const colorMenu = document.createElement('div')
  colorMenu.className = 'tiptap-toolbar-menu'
  FORE_COLORS.forEach(({ label, value }) => {
    const swatch = document.createElement('button')
    swatch.type = 'button'
    swatch.className = 'tiptap-color-swatch'
    swatch.title = label
    swatch.style.backgroundColor = value
    swatch.addEventListener('mousedown', (e) => e.preventDefault())
    swatch.addEventListener('click', (e) => {
      e.preventDefault()
      editor.chain().focus().setColor(value).run()
      colorWrap.classList.remove('is-open')
    })
    colorMenu.appendChild(swatch)
  })
  const clearColor = document.createElement('button')
  clearColor.type = 'button'
  clearColor.className = 'tiptap-toolbar-menu-item'
  clearColor.textContent = 'Alapértelmezett'
  clearColor.addEventListener('mousedown', (e) => e.preventDefault())
  clearColor.addEventListener('click', (e) => {
    e.preventDefault()
    editor.chain().focus().unsetColor().run()
    colorWrap.classList.remove('is-open')
  })
  colorMenu.appendChild(clearColor)
  colorWrap.append(colorBtn, colorMenu)
  bar.append(colorWrap, sep())

  bar.append(
    mkBtn('Felsorolás', 'bi bi-list-ul', () => editor.chain().focus().toggleBulletList().run(), {
      activeCheck: () => editor.isActive('bulletList'),
    }),
    mkBtn('Számozott lista', 'bi bi-list-ol', () => editor.chain().focus().toggleOrderedList().run(), {
      activeCheck: () => editor.isActive('orderedList'),
    }),
    sep(),
    mkBtn('Link', 'bi bi-link-45deg', () => {
      const prev = editor.getAttributes('link').href || ''
      const url = window.prompt('Link URL', prev)
      if (url === null) return
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run()
        return
      }
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }, { activeCheck: () => editor.isActive('link') }),
  )

  // Image file picker
  const fileInput = document.createElement('input')
  fileInput.type = 'file'
  fileInput.accept = 'image/*'
  fileInput.multiple = true
  fileInput.className = 'd-none'
  fileInput.addEventListener('change', () => {
    const files = Array.from(fileInput.files || [])
    if (files.length) ingestImageFiles(entry, files)
    fileInput.value = ''
  })
  bar.append(
    mkBtn('Kép', 'bi bi-image', () => fileInput.click()),
    fileInput,
  )

  document.addEventListener('click', (e) => {
    if (!colorWrap.contains(e.target)) colorWrap.classList.remove('is-open')
  })

  return bar
}

/**
 * Initialize a TipTap instance for a given textarea element.
 *
 * @param {string|HTMLTextAreaElement} editorElOrSelector
 * @param {object} options
 * @returns {Promise<Editor|null>}
 */
export function initTipTap(editorElOrSelector, {
  onImageUploadError,
  height,
  placeholder,
  initialContent,
  onInput,
  imagesUploadUrl,
  onSetup,
  placeCaretAtStart,
} = {}) {
  const textarea = resolveTextarea(editorElOrSelector)
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!textarea?.id) return resolve(null)

      const editorId = textarea.id
      const existing = editors.get(editorId)
      if (existing) {
        // Stale DOM (Turbo) — tear down
        if (!document.body.contains(existing.shell) && !document.body.contains(textarea)) {
          removeTipTap(editorId)
        } else if (document.body.contains(existing.shell)) {
          existing.editor.commands.focus()
          return resolve(existing.editor)
        } else {
          removeTipTap(editorId)
        }
      }

      let content = initialContent
      if (content == null || content === '') {
        content = textarea.value || ''
      }
      content = normalizeTipTapHtml(content)
      if (!content || content === '<p></p>') {
        // keep empty doc
      }

      const isMobile = window.innerWidth < 768
      const editorHeight = height || (isMobile ? 150 : 200)

      const uploadUrl = imagesUploadUrl || buildImageUploadPath(emailApiBase())
      const uploadToEmailApi = !imagesUploadUrl

      // Hide original textarea; TipTap shell takes its place visually.
      textarea.classList.add('tiptap-source-textarea')
      textarea.setAttribute('aria-hidden', 'true')
      textarea.tabIndex = -1

      const shell = document.createElement('div')
      shell.className = 'tiptap-editor-shell'
      shell.dataset.editorId = editorId
      if (editorHeight) {
        shell.style.minHeight = `${editorHeight}px`
      }

      const contentEl = document.createElement('div')
      contentEl.className = 'tiptap-editor-content'
      if (editorHeight) {
        contentEl.style.minHeight = `${Math.max(80, editorHeight - 42)}px`
      }

      /** @type {TipTapEntry} */
      const entry = {
        editor: null,
        shell,
        textarea,
        pendingUploads: new Set(),
        uploadUrl,
        uploadToEmailApi,
        onImageUploadError,
      }

      const editor = new Editor({
        element: contentEl,
        extensions: [
          StarterKit.configure({
            heading: false,
            codeBlock: false,
            code: false,
            horizontalRule: false,
          }),
          Underline,
          TextStyle,
          Color,
          Link.configure({
            openOnClick: false,
            HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
          }),
          Image.configure({
            inline: true,
            allowBase64: true,
          }),
          Placeholder.configure({
            placeholder: placeholder || '',
          }),
        ],
        content: content || '',
        editorProps: {
          attributes: {
            class: 'tiptap ProseMirror',
            'aria-label': placeholder || 'Rich text editor',
          },
          handlePaste(_view, event) {
            const items = Array.from(event.clipboardData?.items || [])
            const files = items
              .filter((i) => i.type.startsWith('image/'))
              .map((i) => i.getAsFile())
              .filter(Boolean)
            if (!files.length) return false
            event.preventDefault()
            ingestImageFiles(entry, files)
            return true
          },
          handleDrop(_view, event) {
            const files = Array.from(event.dataTransfer?.files || [])
              .filter((f) => f.type.startsWith('image/'))
            if (!files.length) return false
            event.preventDefault()
            ingestImageFiles(entry, files)
            return true
          },
        },
        onUpdate: () => {
          syncEntryToTextarea(entry)
          onInput?.()
        },
      })

      entry.editor = editor
      const toolbar = buildToolbar(editor, entry)
      shell.append(toolbar, contentEl)
      textarea.insertAdjacentElement('afterend', shell)
      textarea.value = editor.getHTML()

      editors.set(editorId, entry)

      editor.commands.focus(placeCaretAtStart ? 'start' : undefined)
      onSetup?.(editor)
      resolve(editor)
    }))
  })
}

/**
 * Destroy TipTap instance for an editor id / textarea.
 *
 * @param {string|HTMLElement} editorElOrSelector
 */
export function removeTipTap(editorElOrSelector) {
  const id = resolveEditorId(editorElOrSelector)
  if (!id) return
  const entry = editors.get(id)
  if (!entry) return
  try { entry.editor.destroy() } catch (_) { /* ignore */ }
  entry.shell?.remove()
  entry.textarea?.classList.remove('tiptap-source-textarea')
  entry.textarea?.removeAttribute('aria-hidden')
  if (entry.textarea) entry.textarea.tabIndex = 0
  editors.delete(id)
}

/**
 * Flush TipTap content to its underlying textarea.
 *
 * @param {string} [editorId]
 * @returns {string|undefined}
 */
export function syncTipTap(editorId) {
  if (editorId) {
    const entry = editors.get(editorId)
    if (!entry) return
    syncEntryToTextarea(entry)
    return entry.editor.getHTML()
  }
  editors.forEach((entry) => syncEntryToTextarea(entry))
}

/**
 * Await pending image uploads, then return editor HTML.
 *
 * @param {string} editorId
 * @returns {Promise<string>}
 */
export async function flushTipTapUploads(editorId) {
  if (!editorId) return ''
  const entry = editors.get(editorId)
  if (!entry) {
    return document.getElementById(editorId)?.value || ''
  }

  try {
    await Promise.allSettled([...entry.pendingUploads])
  } catch (_) {
    // Remaining blob: detected by htmlHasBlobImages
  }

  syncEntryToTextarea(entry)
  return entry.editor?.getHTML?.() || entry.textarea?.value || ''
}

/**
 * True when the editor still has in-flight image uploads.
 *
 * @param {string} editorId
 * @returns {boolean}
 */
export function hasPendingTipTapUploads(editorId) {
  if (!editorId) return false
  const entry = editors.get(editorId)
  return Boolean(entry?.pendingUploads?.size)
}

/**
 * Read TipTap HTML content for an editor id (falls back to textarea value).
 *
 * @param {string} editorId
 * @returns {string}
 */
export function getTipTapContent(editorId) {
  if (!editorId) return ''
  const entry = editors.get(editorId)
  if (entry) return entry.editor.getHTML()
  return document.getElementById(editorId)?.value || ''
}

/**
 * Insert HTML at the current selection.
 *
 * @param {string} editorId
 * @param {string} html
 */
export function insertTipTapHtml(editorId, html) {
  const editor = getTipTapEditor(editorId)
  if (!editor) return
  editor.chain().focus().insertContent(normalizeTipTapHtml(html || '')).run()
  syncTipTap(editorId)
}
